import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarWeek,
  faHistory,
  faSpinner,
  faChevronDown,
  faChevronRight,
  faTruck,
  faBox,
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { orderService } from '../../services/order';
import WeeklyOrdersSection from './WeeklyOrdersSection';

const STATUS_CLASSES = {
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-600',
  pending:   'bg-yellow-100 text-yellow-700',
};

const DAY_NAMES = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

const weekdayOf = (dateStr) => dateStr ? DAY_NAMES[new Date(dateStr + 'T00:00:00Z').getUTCDay()] : '—';
const formatDate = (dateStr) => dateStr ? new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-PT') : '—';

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
const RecurringOrdersAdmin = () => {
  const [activeTab, setActiveTab] = useState('weekly'); // 'weekly' | 'history'

  const [chains, setChains]   = useState([]); // [{ root, orders }]
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // id da raiz expandida

  const loadChains = useCallback(async () => {
    setLoading(true);
    try {
      const data = await orderService.getWeeklyChains();
      setChains(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Erro ao carregar histórico de encomendas semanais: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'history') loadChains();
  }, [activeTab, loadChains]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* ---- CABEÇALHO ---- */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <FontAwesomeIcon icon={faCalendarWeek} className="text-blue-600 text-xl" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-neutral-900">Encomendas Semanais</h1>
            <p className="text-xs text-neutral-500">Gestão e histórico de recorrências · Acesso restrito</p>
          </div>
        </div>
      </div>

      {/* ---- TABS ---- */}
      <div className="flex gap-1 mb-6 border-b border-neutral-200">
        <button
          onClick={() => setActiveTab('weekly')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'weekly'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-neutral-500 hover:text-neutral-700'
          }`}
        >
          <FontAwesomeIcon icon={faCalendarWeek} />
          Encomendas Semanais
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'history'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-neutral-500 hover:text-neutral-700'
          }`}
        >
          <FontAwesomeIcon icon={faHistory} />
          Histórico por Cliente
        </button>
      </div>

      {activeTab === 'weekly' && <WeeklyOrdersSection />}

      {/* ---- HISTÓRICO POR CLIENTE ----
          Para cada encomenda "mãe" (ativa ou já cancelada), mostra todas as
          encomendas geradas a partir dela — a cadeia completa, não só o
          template que a originou. */}
      {activeTab === 'history' && (
        <>
          <p className="text-xs text-neutral-500 max-w-2xl mb-4">
            Cada linha é uma encomenda "mãe". Expande para ver todas as encomendas que o
            scheduler gerou a partir dela — dia da semana, data de entrega e estado.
          </p>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <FontAwesomeIcon icon={faSpinner} spin className="text-blue-500 text-2xl mr-3" />
              <span className="text-neutral-500">A carregar...</span>
            </div>
          ) : chains.length === 0 ? (
            <div className="bg-white border border-neutral-200 rounded-lg py-16 text-center">
              <FontAwesomeIcon icon={faHistory} className="text-neutral-300 text-4xl mb-3" />
              <p className="text-neutral-500 text-sm">Nenhuma encomenda semanal encontrada</p>
            </div>
          ) : (
            <div className="space-y-3">
              {chains.map(({ root, orders: childOrders }) => (
                <div key={root.id} className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpanded(prev => prev === root.id ? null : root.id)}
                    className="w-full p-4 flex flex-col sm:flex-row sm:items-center gap-3 text-left hover:bg-neutral-50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <FontAwesomeIcon icon={expanded === root.id ? faChevronDown : faChevronRight} className="text-neutral-400" />
                        <span className="font-semibold text-neutral-900 text-sm">{root.client_name}</span>
                        <span className="text-xs text-neutral-400">{root.order_number} (mãe)</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          root.is_weekly ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-500'
                        }`}>
                          {root.is_weekly ? 'Ativa' : 'Cancelada'}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 mt-1.5 ml-5 text-xs text-neutral-500">
                        <span className="flex items-center gap-1">
                          <FontAwesomeIcon icon={faTruck} />
                          Dia original: {weekdayOf(root.delivery_date)} ({formatDate(root.delivery_date)})
                        </span>
                        <span className="flex items-center gap-1">
                          <FontAwesomeIcon icon={faBox} />
                          {childOrders.length} encomenda{childOrders.length !== 1 ? 's' : ''} gerada{childOrders.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </button>

                  {expanded === root.id && (
                    <div className="border-t border-neutral-100 bg-neutral-50 px-4 py-3">
                      {childOrders.length === 0 ? (
                        <p className="text-xs text-neutral-400">Ainda nenhuma encomenda gerada a partir desta mãe</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-xs">
                            <thead>
                              <tr className="text-neutral-500 border-b border-neutral-200">
                                <th className="pb-1 pr-4 text-left font-medium">Encomenda</th>
                                <th className="pb-1 pr-4 text-left font-medium">Dia da semana</th>
                                <th className="pb-1 pr-4 text-left font-medium">Entrega</th>
                                <th className="pb-1 pr-4 text-left font-medium">Estado</th>
                                <th className="pb-1 text-left font-medium">Criada em</th>
                              </tr>
                            </thead>
                            <tbody>
                              {childOrders.map(order => (
                                <tr key={order.id} className="border-b border-neutral-100 last:border-0">
                                  <td className="py-1 pr-4 font-medium text-blue-700">{order.order_number}</td>
                                  <td className="py-1 pr-4 text-neutral-600">{weekdayOf(order.delivery_date)}</td>
                                  <td className="py-1 pr-4 text-neutral-600">{formatDate(order.delivery_date)}</td>
                                  <td className="py-1 pr-4">
                                    <span className={`px-1.5 py-0.5 rounded text-xs ${STATUS_CLASSES[order.delivery_status] || 'bg-neutral-100 text-neutral-600'}`}>
                                      {order.delivery_status}
                                    </span>
                                  </td>
                                  <td className="py-1 text-neutral-500">
                                    {order.created_at ? new Date(order.created_at).toLocaleString('pt-PT') : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RecurringOrdersAdmin;
